import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { Trash2, Calendar, Clock, Users, ShoppingBag, Plus } from "lucide-react";
import { Layout } from "../components/Layout";
import { useI18n } from "../i18n";
import { useCart } from "../cart";
import { eur, formatDate } from "../lib/format";

export default function VacationBuilder() {
  const { t, lang } = useI18n();
  const { items, removeItem, total } = useCart();
  const navigate = useNavigate();

  const byDate = items.reduce((acc, it) => {
    (acc[it.date] = acc[it.date] || []).push(it);
    return acc;
  }, {});
  const dates = Object.keys(byDate).sort();

  return (
    <Layout>
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-10">
        <div className="text-xs font-bold uppercase tracking-[0.2em] text-secondary mb-2">Vacation Builder</div>
        <h1 className="text-3xl sm:text-4xl font-heading font-semibold tracking-tight text-slate-900">{t("cart.title")}</h1>

        {items.length === 0 ? (
          <div className="mt-12 text-center py-16 rounded-3xl border border-dashed border-slate-200" data-testid="cart-empty">
            <ShoppingBag className="h-12 w-12 text-slate-200 mx-auto mb-4" />
            <p className="text-slate-500">{t("cart.empty")}</p>
            <Link to="/esperienze" className="mt-5 inline-flex items-center gap-2 rounded-full bg-primary text-white font-semibold px-6 py-3"><Plus className="h-4 w-4" /> {t("nav.experiences")}</Link>
          </div>
        ) : (
          <div className="grid lg:grid-cols-3 gap-8 mt-8">
            <div className="lg:col-span-2 space-y-8" data-testid="cart-itinerary">
              {dates.map((date, di) => (
                <div key={date}>
                  <div className="flex items-center gap-2 mb-3">
                    <span className="h-7 w-7 rounded-full bg-primary text-white text-xs font-bold flex items-center justify-center">{di + 1}</span>
                    <span className="font-heading font-semibold text-slate-900 capitalize">{formatDate(date, lang)}</span>
                  </div>
                  <div className="space-y-3 pl-9">
                    {byDate[date].map((it) => (
                      <div key={it._key} data-testid="cart-item" className="flex gap-4 rounded-2xl border border-slate-100 p-3 bg-white shadow-sm">
                        <img src={it.image} alt={it.title} className="h-20 w-24 rounded-xl object-cover shrink-0" />
                        <div className="flex-1 min-w-0">
                          <h3 className="font-heading font-semibold text-slate-900 line-clamp-1">{it.title}</h3>
                          <div className="flex flex-wrap gap-3 text-xs text-slate-500 mt-1">
                            <span className="flex items-center gap-1"><Clock className="h-3 w-3" />{it.time}</span>
                            <span className="flex items-center gap-1"><Users className="h-3 w-3" />{it.participants}</span>
                          </div>
                          <div className="flex items-center justify-between mt-2">
                            <span className="font-bold text-primary">{eur(it.subtotal)}</span>
                            <button onClick={() => removeItem(it._key)} data-testid="remove-item-btn" className="flex items-center gap-1 text-xs text-slate-400 hover:text-destructive transition-colors"><Trash2 className="h-3.5 w-3.5" /> {t("cart.remove")}</button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
              <Link to="/esperienze" className="inline-flex items-center gap-2 text-sm font-semibold text-primary"><Plus className="h-4 w-4" /> {t("cart.addanother")}</Link>
            </div>

            <div className="lg:col-span-1">
              <div className="lg:sticky lg:top-24 rounded-3xl border border-slate-100 shadow-lg p-6 bg-white">
                <h3 className="font-heading font-semibold text-slate-900 mb-4">{t("cart.summary")}</h3>
                <div className="space-y-2 text-sm text-slate-600 mb-4">
                  <div className="flex justify-between"><span>{t("cart.experiences")}</span><span>{items.length}</span></div>
                  <div className="flex justify-between"><span>{t("cart.days")}</span><span>{dates.length}</span></div>
                </div>
                <div className="flex items-center justify-between py-3 border-t border-slate-100">
                  <span className="font-semibold text-slate-800">{t("cart.total")}</span>
                  <span data-testid="cart-total" className="text-2xl font-heading font-bold text-primary">{eur(total)}</span>
                </div>
                <button onClick={() => navigate("/checkout")} data-testid="checkout-btn" className="w-full mt-4 rounded-full bg-primary text-white font-semibold py-3.5 hover:bg-primary/90 transition-colors">{t("cart.checkout")}</button>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}
